<template>
  <div
    class="sub-row-grid-sm grid w-full gap-2 text-gray-500 xl:grid-cols-6"
    data-cy="do-history-sub-row"
  >
    <div class="mr-2 h-full justify-self-end border-l-2 border-gray-200"></div>
    <div class="flex space-x-2">
      <img
        v-if="serviceImage"
        class="h-5 w-5"
        :src="serviceImage"
        :alt="$t('ORCHESTRATION.SERVICE_IMAGE_ALT')"
        loading="lazy"
      />
      <span class="w-8/12 truncate xl:w-full">
        {{
          attribute?.service ||
          `${$t("COMMON.SERVICE")} ${$t("COMMON.NOT_FOUND")}`
        }}
      </span>
    </div>
    <div
      :title="attribute?.name"
      class="col-span-2 flex space-x-0.5 overflow-hidden break-words text-ellipsis whitespace-nowrap"
    >
      <div class="max-w-3/4 truncate">
        {{ attribute?.name || $t("COMMON.NOT_FOUND") }}
      </div>
      <icon-base
        v-if="customAttributeId && attribute"
        :icon="IconPopup"
        class="z-6 cursor-pointer text-gray-400"
        data-cy="attr-link"
        @click="openAttributeInNewTab"
      />
    </div>

    <div class="flex flex-col items-end" data-cy="do-history-expected-value">
      <lf-badge
        v-for="(value, index) in expectedValue"
        :key="value"
        class="max-w-full bg-gray-200 text-xxs font-semibold uppercase"
      >
        <span
          ref="valueRef"
          v-tooltip="{
            content: value,
            onShow: () => isValueTextTruncated[index],
          }"
          class="w-full overflow-hidden text-ellipsis"
        >
          {{ value }}
        </span>
      </lf-badge>
    </div>
    <div class="justify-self-end">
      <lf-badge
        v-tooltip="contextLogTooltip"
        class="text-xxs font-semibold uppercase"
        :class="[
          contextLog?.attribute_result === false
            ? 'failed-attribute-result'
            : 'bg-gray-200',
        ]"
        data-cy="do-history-returned-value"
      >
        {{ getAttributeResultValue(contextLog) }}
      </lf-badge>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { isBaselayerBusinessRegistrationsAttribute } from "@/helpers/baselayer";
import { convertToArray, getObjectKeys } from "@/helpers/common";
import {
  ATTRIBUTE_SYMBOLS,
  SERVICE_CUSTOM,
  TYPE_BOOLEAN,
  TYPE_NOT_NULL,
  TYPE_NULL,
} from "@/helpers/constants";
import { truncateWithEllipsis } from "@/helpers/formatting";
import { isOcrolusFullResponseAttribute } from "@/helpers/ocrolus";
import { getProviderImageByServiceName } from "@/helpers/services";
import { getFullResponseDebtSchedulePlaceholder } from "@/helpers/services";
import { useDeals } from "@/hooks/deals";
import { useElementStatus } from "@/hooks/elements";
import { useAttributesOptionsMap } from "@/hooks/orchestration";
import { ROUTE_CUSTOM_ATTRIBUTES_BUILDER_VIEW } from "@/router/routes";

import IconPopup from "@/components/icons/IconPopup.vue";
import LfBadge from "@/components/ui/LfBadge.vue";

import type {
  IDataOrchestrationHistoryStepContext,
  IDataOrchestrationLogEntry,
} from "@/models/applications";
import type {
  IDataOrchestrationFlowCheckCondition,
  IDataOrchestrationFlowOutcome,
  NaicsRange,
} from "@/models/orchestration";
import type { ComputedRef } from "vue";

const { condition, log, outcome } = defineProps<{
  condition: IDataOrchestrationFlowCheckCondition;
  log: IDataOrchestrationLogEntry;
  outcome: IDataOrchestrationFlowOutcome;
}>();

const UNDERSCORE_DASH_REGEX = /[-_]/g;

const { allAttributes, findCustomServiceAttribute } = useAttributesOptionsMap();
const { t } = useI18n();
const { activeDeal } = useDeals();
const router = useRouter();
const { isTextTruncated } = useElementStatus();

const valueRef = ref<HTMLElement[] | null>(null);
const isValueTextTruncated = isTextTruncated(valueRef) as ComputedRef<
  boolean[]
>;

const attribute = computed(() => {
  const { attribute } = condition;
  if (attribute === SERVICE_CUSTOM) {
    return findCustomServiceAttribute(
      (condition.context?.value as { id: string }).id
    );
  }
  return allAttributes.value[attribute];
});

const contextLogTooltip = computed(() => ({
  content: contextLog.value?.value?.toString(),
  onShow: () => (contextLog.value?.value?.toString()?.length || 0) > 15,
}));

const serviceImage = computed(() => {
  if (!attribute.value?.service) {
    return "";
  }
  return getProviderImageByServiceName(attribute.value.service) || "";
});

const contextLog = computed(() => {
  return log.context?.log?.find(
    ({ outcome: logOutcome, attribute, context }) => {
      const mainCheck =
        logOutcome === outcome.outcome && attribute === condition.attribute;
      if (!context) {
        return mainCheck;
      }
      const keys = getObjectKeys(context.value || {});
      return (
        mainCheck &&
        keys.every((key) => {
          const logContextValue = convertToArray(context.value?.[key]);
          const conditionContextValue = convertToArray(
            condition.context?.value?.[key]
          );
          return logContextValue.every(
            (value, index) => value === conditionContextValue[index]
          );
        })
      );
    }
  );
});

const customAttributeId = computed(() => {
  return condition.context?.value?.id;
});

const openAttributeInNewTab = () => {
  if (!customAttributeId.value) {
    return;
  }
  const { href } = router.resolve({
    name: ROUTE_CUSTOM_ATTRIBUTES_BUILDER_VIEW,
    params: { id: customAttributeId.value },
    query: { evaluate: activeDeal.value.id },
  });

  const link = Object.assign(document.createElement("a"), {
    href,
    target: "_blank",
    rel: "noopener noreferrer",
  });

  link.click();
};

const getValueWithPrefixAndSuffix = (
  value: string | number | boolean | null | undefined | NaicsRange
) => {
  if (value === null) {
    return String(value);
  }
  if (
    value &&
    typeof value !== "string" &&
    typeof value !== "number" &&
    typeof value !== "boolean"
  ) {
    return `${value.from} - ${value.through} (${value.digits} ${t(
      "COMMON.DIGITS"
    )})`;
  }
  const prefix = attribute.value?.meta.unit.prefix || "";
  const suffix = attribute.value?.meta.unit.suffix || "";
  return `${prefix}${value}${suffix}`;
};

const expectedValue = computed(() => {
  return condition.conditions.reduce<Array<string>>((acc, { type, value }) => {
    const parsedValue = Array.isArray(value)
      ? value.map(getValueWithPrefixAndSuffix)
      : getValueWithPrefixAndSuffix(value);

    let prefix = [TYPE_BOOLEAN, TYPE_NULL].includes(type)
      ? ""
      : ATTRIBUTE_SYMBOLS[type] || type;
    prefix = prefix.replace(UNDERSCORE_DASH_REGEX, " ");

    if (type === TYPE_NOT_NULL) {
      acc.push(prefix);
      return acc;
    }

    if (Array.isArray(parsedValue)) {
      const withPrefix = parsedValue.map((str) => `${prefix} ${str}`);
      acc.push(...withPrefix);
    } else {
      acc.push(`${prefix} ${parsedValue}`);
    }
    return acc;
  }, []);
});

const getAttributeResultValue = (
  contextLog: IDataOrchestrationHistoryStepContext | undefined
) => {
  if (!contextLog) {
    return t("ORCHESTRATION.NOT_EVALUATED");
  }

  if (isOcrolusFullResponseAttribute(contextLog)) {
    return t(
      "DEALS.DEAL_PROGRESS.UNDERWRITING.OCROLUS.SERVICE_RUN_ATTRIBUTE_PLACEHOLDER"
    );
  }

  if (isBaselayerBusinessRegistrationsAttribute(contextLog)) {
    return t("DEALS.BASELAYER.BUSINESS_REGISTRATIONS_JSON");
  }

  const fullResponseDebtSchedulePlaceholder =
    getFullResponseDebtSchedulePlaceholder(contextLog.value);

  if (fullResponseDebtSchedulePlaceholder) {
    return fullResponseDebtSchedulePlaceholder;
  }

  return contextLog.value === null
    ? String(contextLog.value).toUpperCase()
    : truncateWithEllipsis(contextLog.value?.toString() || "", 15);
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.failed-attribute-result {
  @apply bg-red-100! text-red-500!;
}

@media only screen and (max-width: 1279px) {
  .sub-row-grid-sm {
    grid-template-columns: 1fr minmax(100px, 1fr) 0.5fr 0.5fr 1fr 1fr;
  }
}
</style>
